import { z } from "zod";
import type { AiEstimate, MaterialGrade, PriceSnapshot } from "../estimate.js";
import { MATERIAL_GRADES, polygonSchema } from "../estimate.js";
import { aiToGroupInputs, computeEstimate, type EstimateGroupInput, type EstimateInput, type EstimateResult } from "../pricing/computeEstimate.js";
import { JOB_STATUSES, type JobStatus } from "./stateMachine.js";

const money = z.number().nonnegative().max(100_000_000);

/** ค่าดำเนินการ/กำไรตั้งต้น (%) ตามที่ตกลง; ภายหลังย้ายเป็น config ที่ Admin ปรับได้ (Phase 4) */
export const DEFAULT_OVERHEAD_PCT = 20;

/** การแก้ไขของผู้ใช้ต่อหนึ่งพื้นที่ทำงาน (areaIndex = ลำดับใน ai.work_areas) */
export const groupEditSchema = z.object({
  areaIndex: z.number().int().min(0).max(7),
  polygon: polygonSchema.optional(),
  dims: z
    .object({
      width_m: z.number().nonnegative().max(500).nullable().optional(),
      length_m: z.number().nonnegative().max(500).nullable().optional(),
      height_m: z.number().nonnegative().max(100).nullable().optional(),
      count: z.number().int().nonnegative().max(10000).nullable().optional(),
      area_m2: z.number().nonnegative().max(100000).nullable().optional(),
      doorDeduct_m2: z.number().nonnegative().max(1000).optional(),
    })
    .optional(),
  wastePct: z.number().min(0).max(1).optional(),
  tileSizeM: z.number().positive().max(5).optional(),
  overrides: z
    .object({
      laborRate: money.optional(),
      materialRate: money.optional(),
      qty: z.record(z.string().max(60), z.number().nonnegative().max(1_000_000)).optional(),
      unitPrice: z.record(z.string().max(60), money).optional(),
      removed: z.array(z.string().max(60)).max(60).optional(),
    })
    .optional(),
  custom: z
    .array(z.object({ key: z.string().min(1).max(60), name_th: z.string().min(1).max(120), qty: z.number().nonnegative().max(1_000_000), unit: z.string().max(20), unitPrice: money }))
    .max(40)
    .optional(),
});
export type GroupEdit = z.infer<typeof groupEditSchema>;

export const jobEditsSchema = z.object({
  grade: z.enum(MATERIAL_GRADES).optional(),
  discount: z.object({ type: z.enum(["amount", "percent"]), value: z.number().nonnegative().max(100_000_000) }).optional(),
  overheadPct: z.number().min(0).max(100).optional(),
  vat: z.boolean().optional(),
  groups: z.array(groupEditSchema).max(8).default([]),
});
export type JobEdits = z.infer<typeof jobEditsSchema>;

/**
 * สร้าง EstimateInput จากผล AI + ราคา ณ วันประเมิน + การแก้ไขของผู้ใช้ ใช้ฟังก์ชันเดียวกันทั้ง client และ server
 * edits = undefined คือค่า AI ล้วน (ai_value)
 */
export function buildEstimateInput(ai: AiEstimate, snapshots: readonly PriceSnapshot[], edits?: JobEdits, defaults?: { overheadPct?: number }): EstimateInput {
  const grade: MaterialGrade = edits?.grade ?? ai.recommended_material_grade;
  const { groups } = aiToGroupInputs(ai, snapshots, grade);
  const byArea = new Map((edits?.groups ?? []).map((g) => [g.areaIndex, g]));
  const merged: EstimateGroupInput[] = groups.map((g, i) => {
    // id ของกลุ่มจาก aiToGroupInputs คือ area-<index> ตรงกับ areaIndex ใน ai.work_areas
    const idx = Number(String(g.id ?? "").replace("area-", ""));
    const e = byArea.get(Number.isInteger(idx) ? idx : i);
    if (!e) return g;
    const dimsEdited = e.dims !== undefined;
    return {
      ...g,
      dims: { ...g.dims, ...(e.dims ?? {}) },
      dimsSource: dimsEdited ? "user" : g.dimsSource,
      wastePct: e.wastePct ?? g.wastePct,
      tileSizeM: e.tileSizeM ?? g.tileSizeM,
      overrides: e.overrides ?? g.overrides,
      custom: e.custom ?? g.custom,
    };
  });
  return { groups: merged, discount: edits?.discount, overheadPct: edits?.overheadPct ?? defaults?.overheadPct ?? DEFAULT_OVERHEAD_PCT, vat: edits?.vat };
}

export function computeJobResults(ai: AiEstimate, snapshots: readonly PriceSnapshot[], edits?: JobEdits, defaults?: { overheadPct?: number }): { ai_value: EstimateResult; user_value: EstimateResult } {
  return { ai_value: computeEstimate(buildEstimateInput(ai, snapshots, undefined, defaults)), user_value: computeEstimate(buildEstimateInput(ai, snapshots, edits, defaults)) };
}

/** ----- สัญญา API งาน (Phase 3) ----- */
export const customerSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: z.string().trim().max(30).optional(),
  address: z.string().trim().max(300).optional(),
});

/** POST /jobs  402 {error:"quota", reason:"save_limit"} เมื่อ Free บันทึกครบ */
export const saveJobRequestSchema = z.object({
  estimateId: z.string().uuid(),
  title: z.string().trim().min(1).max(160),
  customer: customerSchema,
  edits: jobEditsSchema.optional(),
});
export type SaveJobRequest = z.infer<typeof saveJobRequestSchema>;

/** PUT /jobs/{id}/estimate  บันทึกค่าที่แก้ใหม่เป็นเวอร์ชันถัดไป (ไม่ใช้ AI ไม่หักโควตา) */
export const updateEditsRequestSchema = z.object({ edits: jobEditsSchema });

export const patchJobRequestSchema = z.object({
  title: z.string().trim().min(1).max(160).optional(),
  customer: customerSchema.optional(),
  plannedStart: z.string().date().nullable().optional(),
  plannedFinish: z.string().date().nullable().optional(),
  actualStart: z.string().date().nullable().optional(),
  actualFinish: z.string().date().nullable().optional(),
  progressOverride: z.number().int().min(0).max(99).nullable().optional(),
});

export const changeStatusRequestSchema = z.object({
  to: z.enum(JOB_STATUSES),
  note: z.string().trim().max(500).optional(),
  photoKey: z.string().max(200).optional(),
});

export const milestoneRequestSchema = z.object({
  label: z.string().trim().min(1).max(80),
  amount_thb: z.number().positive().max(100_000_000),
  dueDate: z.string().date().optional(),
});
export const patchMilestoneRequestSchema = z.object({
  label: z.string().trim().min(1).max(80).optional(),
  amount_thb: z.number().positive().max(100_000_000).optional(),
  dueDate: z.string().date().nullable().optional(),
  paidAt: z.string().date().nullable().optional(),
});

export interface JobStatusEventDto {
  eventId: string;
  from: JobStatus | null;
  to: JobStatus;
  note?: string;
  photoUrl?: string;
  by: string;
  at: string;
}
export interface MilestoneDto {
  milestoneId: string;
  label: string;
  amount_thb: number;
  dueDate?: string;
  paidAt?: string;
}
export interface JobSummaryDto {
  jobId: string;
  title: string;
  customerName: string;
  customerAddress?: string;
  /** รูปแรกของงาน (presigned อายุสั้น) */
  thumbUrl?: string;
  status: JobStatus;
  progress: number;
  grandTotal: number;
  categoryIds: string[];
  createdAt: string;
  updatedAt: string;
}
export interface JobDetailDto extends JobSummaryDto {
  customer: z.infer<typeof customerSchema>;
  estimateId: string;
  estimateVersion: number;
  /** ผู้ผลิตผลประเมิน (model.id = "manual" คือกรอกขนาดเอง ใช้ isManualModel) */
  model?: { id: string; promptVersion: string };
  beforeHold: string | null;
  progressOverride: number | null;
  dates: { plannedStart?: string; plannedFinish?: string; actualStart?: string; actualFinish?: string };
  ai: AiEstimate;
  priceSnapshots: PriceSnapshot[];
  photos: { key: string; url: string }[];
  edits: JobEdits;
  results: { ai_value: EstimateResult; user_value: EstimateResult };
  events: JobStatusEventDto[];
  milestones: MilestoneDto[];
  /** อ่านได้อย่างเดียว เมื่อแพ็กเกจเสียเงินหมดอายุ ฯลฯ (ยังเปลี่ยนสถานะได้) */
  quotationId?: string;
}
